import * as React from 'react'
import { memo } from 'react'
import { Modal, useModalContext } from '../index'
import { useAudioNote } from '@renderer/hooks/audio/useAudioNote'
import { useShareAudioNote } from '@renderer/hooks/useShareAudioNote'
import AudioNoteEmailSharer from '@renderer/components/AudioNoteEmailSharer'
import { Copy, CopyCheck, Link2, Trash2 } from 'lucide-react'
import { Button, Tooltip } from '@highlight/ui'
import { isMeetingNote, noteNoun } from '@shared/utils/audio-notes/AudioNoteUtils'

export const AudioNoteEmailModal = () => {
  const { context } = useModalContext('audio-note-email')
  const { data: audioNote } = useAudioNote(context.audioNoteId)

  const { deleteShareLink, handleShare, shareLink, state, isDisabled } = useShareAudioNote({
    audioNote: audioNote || null,
    meetingAppName: audioNote?.meeting?.app?.name,
    eventAction: 'share',
  })

  if (!audioNote) return null

  const noun = noteNoun(isMeetingNote(audioNote))

  return (
    <Modal>
      <Modal.Content className="gap-3 rounded-none border-0 bg-transparent shadow-none">
        {/* Main Section */}
        <div className="overflow-hidden rounded-3xl bg-[#1C1C1C] shadow-lg">
          <Modal.Header>
            <Modal.Title>Share via email</Modal.Title>
            <Modal.Description>
              Send an email with the generated share link. Anyone with the link will be able to view the {noun}.
            </Modal.Description>
          </Modal.Header>

          <Modal.Body>
            <AudioNoteEmailSharer audioNote={audioNote} />
          </Modal.Body>
        </div>

        {/* Secondary Section - Share Link */}
        <div className="overflow-hidden rounded-3xl bg-[#1C1C1C] shadow-lg">
          <Modal.Header>
            <Modal.Title>Share link</Modal.Title>
            <Modal.Description>Anyone with the link will be able to view the {noun}.</Modal.Description>
          </Modal.Header>

          <Modal.Body>
            {shareLink ? (
              <SecondaryShareLinkCard
                state={state}
                shareLink={shareLink}
                onCopy={handleShare}
                onDelete={() => deleteShareLink(audioNote)}
              />
            ) : (
              <SecondaryCreateLinkCard disabled={isDisabled} onCreate={handleShare} />
            )}
          </Modal.Body>
        </div>
      </Modal.Content>
    </Modal>
  )
}

type SecondaryShareLinkCardProps = {
  state: 'default' | 'sharing' | 'copied' | 'shared' | 'deleting'
  shareLink: string
  onCopy: (e?: React.MouseEvent) => void
  onDelete: () => void
}

const SecondaryShareLinkCard = memo(function SecondaryShareLinkCard({
  state,
  shareLink,
  onCopy,
  onDelete,
}: SecondaryShareLinkCardProps) {
  return (
    <div
      onClick={(e) => {
        e.stopPropagation()
        onCopy(e)
      }}
      className="bg-light-5 hover:bg-light-15 flex w-full cursor-pointer items-center gap-2 overflow-hidden text-ellipsis rounded-xl border p-1.5 pl-3 transition-colors"
    >
      <Link2 size={16} />
      <div className="mr-auto max-w-64 truncate">
        {state === 'copied' ? 'Copied!' : state === 'deleting' ? 'Deleting...' : shareLink}
      </div>
      <Tooltip.Provider>
        <Tooltip>
          <Tooltip.Trigger
            render={
              <Button
                variant="outline"
                size="sm"
                shape="square"
                onClick={(e) => {
                  e.stopPropagation()
                  onDelete()
                }}
              />
            }
          >
            <Trash2 />
          </Tooltip.Trigger>
          <Tooltip.Content>Delete share link</Tooltip.Content>
        </Tooltip>
        <Tooltip>
          <Tooltip.Trigger
            render={
              <Button
                variant="outline"
                size="sm"
                shape="square"
                onClick={(e) => {
                  e.stopPropagation()
                  onCopy(e)
                }}
              />
            }
          >
            {state === 'copied' ? <CopyCheck /> : <Copy />}
          </Tooltip.Trigger>
          <Tooltip.Content>Copy link</Tooltip.Content>
        </Tooltip>
      </Tooltip.Provider>
    </div>
  )
})

type SecondaryCreateLinkCardProps = {
  disabled: boolean
  onCreate: (e?: React.MouseEvent) => Promise<void>
}

const SecondaryCreateLinkCard = memo(function SecondaryCreateLinkCard({
  disabled,
  onCreate,
}: SecondaryCreateLinkCardProps) {
  return (
    <div className="bg-light-5 flex items-center justify-between gap-2 rounded-xl border p-1.5 pl-3">
      <div className="flex items-center gap-2">
        <Link2 size={16} className="text-light/40" />
        <div className="flex flex-col">
          <span className="text-light/50 text-sm">Generate a shareable link for this note</span>
        </div>
      </div>
      <Button
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={async (e) => {
          e.stopPropagation()
          await onCreate(e)
        }}
      >
        Create link
      </Button>
    </div>
  )
})
